// src/stores/userStore.js
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getAuth, onAuthStateChanged, signOut, getRedirectResult } from 'firebase/auth'
import { doc } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '@/services/firebaseconfig'
import router from '@/router'
import { useAuthConfigStore } from '@/stores/authConfigStore'
import { reportPermissionDenied } from '@/utils/reportPermissionDenied'
import { notProvisionedRoute } from '@/utils/unprovisionedSignIn'
import { endSession } from '@/services/sessionEnd'
import { readAccountProfile } from '@/utils/readAccountProfile'
import { logEvent } from '@/utils/firebase/eventLogger'
import {
  shouldActivateInvitedStaff,
  isNowActive,
  applyActivatedStatus,
} from '@/utils/staffSignInActivation'

export const useUserStore = defineStore('userStore', () => {
  // ----- State -----
  const user = ref(null)
  const impersonator = ref(null)
  const isInitialized = ref(false)

  // NEW: prevents double-initializing the auth listener
  const hasInitStarted = ref(false)

  let resolveInitialization
  const initializationPromise = new Promise(resolve => {
    resolveInitialization = resolve
  })

  let authUnsubscribe = null
  let visibilityHandler = null
  let authReadVersion = 0

  // ----- Computed -----

  // normalized roles array from V2 nested `roles.userRole`
  const roles = computed(() => {
    const raw = user.value?.roles?.userRole
    if (!raw) return []
    return (Array.isArray(raw) ? raw : [raw])
      .filter(Boolean)
      .map(r => String(r).trim().toLowerCase())
  })

  const isAdmin = computed(() => roles.value.includes('admin'))
  const isSalesUser = computed(() => roles.value.includes('sales'))
  const isCustomer = computed(() => roles.value.includes('customer'))
  // Any token carrying the external-review role fails closed into the isolated
  // reviewer experience. An accidentally mixed role must never fall back to
  // generic staff access; the reviewer callable separately requires the exact
  // one-role + read-only shape before it accepts an action.
  const isMarketingReviewer = computed(() => roles.value.includes('marketing_reviewer'))
  const linkedCustomerId = computed(() => user.value?.linkedCustomerId || null)
  const isImpersonating = computed(() => !!impersonator.value)
  const isReadOnly = computed(
    () => user.value?.roles?.isReadOnly === true || user.value?.isReadOnly === true
  )

  // ✅ Super Admin flag (nested `roles.isSuperAdmin` first, flat fallback, claims override)
  const isSuperAdmin = computed(
    () => user.value?.roles?.isSuperAdmin === true || user.value?.isSuperAdmin === true
  )

  // Staff is a POSITIVE role: no portal link, and super-admin or any role other
  // than 'customer'. The same boundary as firestore.rules isStaff() and the
  // backend's anyStaff gate. "Signed in" is not staff: portal customers are
  // signed in too (docs/bugs/signed_in_is_not_staff_BUG.md, 2026-09-14).
  const isStaff = computed(
    () =>
      !linkedCustomerId.value &&
      !isMarketingReviewer.value &&
      (isSuperAdmin.value || roles.value.some(r => r !== 'customer'))
  )

  // ✅ Explicit Permission Check:
  // Returns true if the field is missing, null, or true.
  // Only returns false if the field is explicitly set to false.
  const canCreateCustomers = computed(() => {
    const nested = user.value?.roles?.canCreateCustomers
    if (nested !== undefined) return nested !== false
    return user.value?.canCreateCustomers !== false
  })

  // ----- Methods -----
  function waitForInitialization() {
    return initializationPromise
  }

  function setUser(userData) {
    user.value = userData
  }

  // ✅ Allows UI to immediately reflect refreshed claims without requiring auth listener
  function setSuperAdmin(val) {
    if (!user.value) return
    user.value = { ...user.value, isSuperAdmin: !!val }
  }

  // ✅ Allows UI to immediately reflect refreshed roles (if you store roles in claims)
  // userRole is always an array of strings post-migration 06
  function setRoles(newRoles) {
    if (!user.value) return
    // Nested under `roles`, because that is where every reader looks — the
    // normalized getter above reads `user.value?.roles?.userRole`. Writing a
    // flat top-level `userRole` here meant a role refresh appeared to succeed
    // and changed nothing: the setter and the getters were addressing two
    // different fields. (Bug bPXW8tBvhwz3o65waQrz, 2026-08-25.)
    user.value = {
      ...user.value,
      roles: { ...(user.value.roles || {}), userRole: newRoles },
    }
  }

  function clearUser() {
    user.value = null
    impersonator.value = null

    // Clear session storage
    try {
      sessionStorage.removeItem('oauthPending')
    } catch {
      // ignore — sessionStorage unavailable (e.g. privacy mode)
    }
    try {
      sessionStorage.removeItem('oauthPhase')
    } catch {
      // ignore — sessionStorage unavailable (e.g. privacy mode)
    }

    // ✅ Don't unsubscribe auth listener here - it needs to stay active
    // to detect when user logs back in. Only unsubscribe on unmount.

    // ✅ Reset initialization state so the next login goes through proper flow
    // But keep hasInitStarted true so we don't re-init the listener
  }

  // A staff member who accepts an invitation by signing in (Google, the portal overlay, a password set outside the
  // invite page, a restored session) is activated by markStaffSignIn: every sign-in page reaches this listener.
  // Fire-and-forget: the session never waits or fails on it. When the server says the account is active (this call or
  // another tab's), the session patches its own copy in place (docs/bugs/staff_sign_in_never_activates_BUG.md).
  function activateInvitedStaff(uid, isCurrentSession) {
    const profile = user.value
    const impersonating = !!impersonator.value
    if (!shouldActivateInvitedStaff({ isStaff: isStaff.value, profile, impersonating })) return
    httpsCallable(functions, 'markStaffSignIn')()
      .then(res => {
        const sameSession = isCurrentSession() && user.value?.uid === uid
        if (sameSession && isNowActive(res?.data)) applyActivatedStatus(user.value)
      })
      .catch(() => {})
  }

  async function initAuthListener() {
    if (hasInitStarted.value) {
      return initializationPromise
    }
    hasInitStarted.value = true

    console.log('[userStore] 1. initAuthListener CALLED.')
    const auth = getAuth()

    if (authUnsubscribe) {
      authUnsubscribe()
    }

    const isImpersonation = sessionStorage.getItem('isImpersonationReload') === 'true'

    if (!isImpersonation) {
      try {
        const result = await getRedirectResult(auth)
        if (result && result.user) {
          try {
            sessionStorage.removeItem('oauthPending')
          } catch {
            // ignore — sessionStorage unavailable (e.g. privacy mode)
          }
          try {
            sessionStorage.removeItem('oauthPhase')
          } catch {
            // ignore — sessionStorage unavailable (e.g. privacy mode)
          }
        } else {
          const atCallback =
            router?.currentRoute?.value?.name === 'AuthCallback' ||
            (router?.currentRoute?.value?.path || '').startsWith('/auth/callback')
          if (atCallback) {
            try {
              sessionStorage.removeItem('oauthPending')
            } catch {
              // ignore — sessionStorage unavailable (e.g. privacy mode)
            }
            try {
              sessionStorage.removeItem('oauthPhase')
            } catch {
              // ignore — sessionStorage unavailable (e.g. privacy mode)
            }
          }
        }
      } catch (error) {
        console.error(
          '[userStore] 3. ERROR processing getRedirectResult():',
          error?.code,
          error?.message
        )
        try {
          sessionStorage.removeItem('oauthPending')
        } catch {
          // ignore — sessionStorage unavailable (e.g. privacy mode)
        }
        try {
          sessionStorage.removeItem('oauthPhase')
        } catch {
          // ignore — sessionStorage unavailable (e.g. privacy mode)
        }
      }
    }

    // Force-refresh the ID token whenever a backgrounded tab becomes visible.
    // Firebase's background refresh loop is throttled/suspended on hidden tabs,
    // so without this a user returning to a long-idle tab can hit silent
    // permission errors (empty search results, failed writes) because the
    // cached token is expired. Config-gated via authConfig.refreshOnVisibility.
    if (typeof document !== 'undefined' && !visibilityHandler) {
      visibilityHandler = async () => {
        if (document.visibilityState !== 'visible') return
        const current = getAuth().currentUser
        if (!current) return
        try {
          const cfg = useAuthConfigStore()
          if (cfg.hasFetched && !cfg.refreshOnVisibility) return
        } catch {
          /* store not initialized yet — refresh anyway, safer default */
        }
        try {
          await current.getIdToken(true)
        } catch (err) {
          console.warn('[userStore] visibility token refresh failed:', err?.code || err?.message)
        }
      }
      document.addEventListener('visibilitychange', visibilityHandler)
    }

    authUnsubscribe = onAuthStateChanged(auth, async firebaseUser => {
      const version = ++authReadVersion
      const isCurrentSession = () =>
        version === authReadVersion && auth.currentUser === firebaseUser
      try {
        if (!isCurrentSession()) return
        if (!firebaseUser) {
          console.log('[userStore] No user signed in')
          user.value = null
          impersonator.value = null

          // ✅ CRITICAL: Still need to resolve initialization even when no user!
          if (!isInitialized.value) {
            isInitialized.value = true
            resolveInitialization()
            console.log('[userStore] Initialization resolved (no user)')
          }
          return
        }

        try {
          sessionStorage.removeItem('oauthPending')
        } catch {
          // ignore — sessionStorage unavailable (e.g. privacy mode)
        }
        try {
          sessionStorage.removeItem('oauthPhase')
        } catch {
          // ignore — sessionStorage unavailable (e.g. privacy mode)
        }

        const idTokenResult = await firebaseUser.getIdTokenResult()
        if (!isCurrentSession()) return
        const claims = idTokenResult.claims
        const impersonatorUid = claims.impersonatorUid

        if (impersonatorUid) {
          impersonator.value = {
            uid: impersonatorUid,
            displayName: claims.impersonatorDisplayName || 'Super Admin',
          }
        } else {
          impersonator.value = null
        }

        const userDocRef = doc(db, 'users', firebaseUser.uid)
        const { snapshot: userDocSnap, diagnostics } = await readAccountProfile(
          userDocRef,
          firebaseUser
        )
        if (!isCurrentSession()) return
        if (!userDocSnap.exists()) {
          // No users/ doc = no CRM account. This branch used to create one with
          // role 'sales' for ANY signed-in account; with public sign-up open that
          // promoted strangers to staff (syncUserClaims copied the role into claims).
          // Accounts are provisioned server-side (inviteUser, registerCustomer,
          // createDirectoryUser), and the rules now refuse a self-created doc.
          // Record the attempt where a super-admin can see it, then sign out.
          console.warn(
            '[userStore] Signed-in account has no users/ doc (not provisioned) — signing out.'
          )
          await reportPermissionDenied(
            {
              kind: 'page',
              key: 'crm-account',
              action: 'navigate',
              message: 'Signed-in account has no users/ doc (not provisioned); signed out.',
              meta: {
                providers: (firebaseUser.providerData || []).map(p => p.providerId),
                ...diagnostics,
              },
            },
            { uid: firebaseUser.uid, email: firebaseUser.email || null }
          )
          if (!isCurrentSession()) return
          user.value = null
          impersonator.value = null
          if (!isInitialized.value) {
            isInitialized.value = true
            resolveInitialization()
          }
          await router.replace(notProvisionedRoute()).catch(() => {})
          if (!isCurrentSession()) return
          await signOut(auth)
          return
        }
        const firestoreData = userDocSnap.data() || {}

        user.value = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName || '',
          ...firestoreData,
          ...claims,
        }
        activateInvitedStaff(firebaseUser.uid, isCurrentSession)

        if (diagnostics.outcome === 'recovered') {
          void logEvent({
            eventType: 'USER_PROFILE_READ_RECOVERED',
            action: 'read',
            resource: { type: 'user', id: firebaseUser.uid },
            context: { metadata: diagnostics },
          })
        }

        // ✅ CRITICAL: Mark as initialized BEFORE any navigation
        // This prevents deadlock where router.replace() triggers a guard
        // that awaits waitForInitialization() which hasn't resolved yet
        if (!isInitialized.value) {
          isInitialized.value = true
          resolveInitialization()
        }

        // ✅ REMOVED: Don't navigate from here - let App.vue handle it
        // This prevents race conditions with App.vue's watcher also trying to redirect
      } catch (err) {
        if (!isCurrentSession()) return
        console.error('[userStore] 9. CRITICAL ERROR in auth listener:', err)
        user.value = null
        impersonator.value = null

        // ✅ Still resolve on error so app doesn't hang
        if (!isInitialized.value) {
          isInitialized.value = true
          resolveInitialization()
        }
        if (err?.code === 'account/profile-unavailable') {
          void logEvent({
            eventType: 'USER_PROFILE_READ_UNAVAILABLE',
            action: 'read',
            resource: { type: 'user', id: firebaseUser.uid },
            context: { metadata: err.profileReadDiagnostics },
          })
          await router
            .replace({ name: 'SignedOut', query: { reason: 'profile-unavailable' } })
            .catch(() => {})
        }
      } finally {
        // An older callback must not clear the next session's transition flag.
        if (isCurrentSession() && sessionStorage.getItem('isImpersonationReload') === 'true') {
          sessionStorage.removeItem('isImpersonationReload')
        }
      }
    })

    return initializationPromise
  }

  function fetchCurrentUser() {
    return initAuthListener()
  }

  /**
   * Sign this tab out: ONE teardown for the whole session (src/services/sessionEnd.js). Every Firestore listener the
   * session opened is closed before auth goes away, so none runs into "Missing or insufficient permissions". Then
   * `redirectTo` loads fresh, so no store keeps this user's listeners or data
   * (docs/bugs/idle_signout_listener_leak_BUG.md). The App-level teardown (voice, in-flight fetches) runs first.
   *
   * @param {object} [opts]
   * @param {string} [opts.redirectTo] where the fresh page loads (default: the signed-out page)
   */
  async function signOutUser({ redirectTo = '/signed-out' } = {}) {
    await endSession({
      redirectTo,
      teardown: () => {
        if (typeof window.__appCleanupSubscriptions === 'function')
          window.__appCleanupSubscriptions()
      },
    })
  }

  return {
    user,
    impersonator,
    isImpersonating,
    roles,
    isAdmin,
    isSalesUser,
    isCustomer,
    isMarketingReviewer,
    isReadOnly,
    linkedCustomerId,
    isStaff,
    isSuperAdmin, // ✅ NEW
    canCreateCustomers,
    isInitialized,
    setUser,
    setSuperAdmin, // ✅ NEW
    setRoles, // ✅ NEW
    clearUser,
    initAuthListener,
    fetchCurrentUser,
    waitForInitialization,
    signOutUser,
  }
})
